import { lazy } from "react";
import { Features, LookupTypes, SapPermissions } from "../app/constants";
import { SalaryComponentCategory } from "../features/employees/employees.types";

const TransactionEntryPage = lazy(() => import("../features/transaction/components/TransactionEntryPage"));
const UserProfilePage = lazy(() => import("../features/users/components/UserProfilePage"));
const TransactionPage = lazy(() => import("../features/transaction/components/TransactionPage"));
const WorkLogsPage = lazy(() => import("../features/worklog/components/WorkLogsPage"));
const MyWorkLogsPage = lazy(() => import("../features/worklog/components/MyWorkLogsPage"));
const ProjectsPage = lazy(() => import("../features/project/components/ProjectsPage"));
const LookupList = lazy(() => import("../features/lookup/components/LooksupList"));
const SmartDashboardPage = lazy(() => import("../features/dashboard/components/smart-dashboard/SmartDashboardPage"));
const HarvestLogsPage = lazy(() => import("../features/harvest/components/HarvestLogsPage"));
const EmployeesPage = lazy(() => import("../features/employees/components/EmployeesPage"));
const WorkLogsSummaryPage = lazy(() => import("../features/worklog/components/WorkLogsSummaryPage"));
const AllTenantsPage = lazy(() => import("../features/tenant/components/AllTenantsPage"));
const TenantInvoicesPage = lazy(() => import("../features/tenant/components/billing/TenantInvoicesPage"));
const PayrollPage = lazy(() => import("../features/payroll/components/PayrollPage"));
const PayrollAddHocPage = lazy(() => import("../features/payroll/components/PayrollAddHocItemsPage"));
const TreesPage = lazy(() => import("../features/crop-tracking/components/trees/TreesPage"));
const CropingSeasonsPage = lazy(
    () => import("../features/crop-tracking/components/croping-seasons/CropingSeasonsPage"),
);
const CropStatsPage = lazy(() => import("../features/crop-tracking/components/crop-stats/CropStatsPage"));
const CropAnalyticsPage = lazy(() => import("../features/crop-tracking/components/crop-stats/CropAnalyticsPage"));
const AttendanceLogsPage = lazy(() => import("../features/attendance/components/AttendanceLogsPage"));
const DevicesPage = lazy(() => import("../features/attendance/components/AttendanceDevicesPage"));
const CurrentTenantPage = lazy(() => import("../features/tenant/components/CurrentTenantPage"));
const LeaveTypesPage = lazy(() => import("../features/leave/components/leave-type/LeaveTypesPage"));
const LeaveManagementPage = lazy(() => import("../features/leave/components/employee-leave-mgmt/LeaveManagementPage"));
const LeaveApprovePage = lazy(() => import("../features/leave/components/leave-request/LeaveRequestsPage"));
const MyLeavePage = lazy(() => import("../features/leave/components/my-leave/MyLeavePage"));
const MyLeaveApprovePage = lazy(() => import("../features/leave/components/my-leave/MyLeaveApprovePage"));
const HolidaysPage = lazy(() => import("../features/config/components/HolidaysPage"));
const StockMarketAnnouncementsPage = lazy(
    () => import("../features/stock-market/components/StockMarketAnnouncementsPage"),
);
const SalaryComponentTypesPage = lazy(() => import("../features/employees/components/SalaryComponentTypesPage"));
const EmployeeWorkSchedulesPage = lazy(() => import("../features/employees/components/EmployeeWorkSchedulesPage"));
const TenantUsersPage = lazy(() => import("../features/tenant/components/TenantUsersPage"));
const TenantConfigsPage = lazy(() => import("../features/config/components/TenantConfigsPage"));
const InvoicesPage = lazy(() => import("../features/invoicing/components/InvoicesPage"));
const VouchersPage = lazy(() => import("../features/voucher/components/VouchersPage"));
const ProductsPage = lazy(() => import("../features/products/components/ProductsPage"));
const CustomersPage = lazy(() => import("../features/customers/components/CustomersPage"));
const HRReportsPage = lazy(() => import("../features/reports/components/HRReportsPage"));
const ShiftPlannerPage = lazy(() => import("../features/worklog/components/shift-planner/ShiftPlannerPage"));
const TenantBillingPage = lazy(() => import("../features/tenant/components/billing/TenantBillingPage"));
const ReferralCodesPage = lazy(() => import("../features/referral/components/ReferralCodesPage"));
const MyReferralsPage = lazy(() => import("../features/referral/components/MyReferralsPage"));
const BranchesPage = lazy(() => import("../features/branch/components/BranchesPage"));
const TeamsPage = lazy(() => import("../features/team/components/TeamsPage"));
const QRCodeManagePage = lazy(() => import("../features/attendance-qr/components/QRCodeManagePage"));
const QRScanPage = lazy(() => import("../features/attendance-qr/components/QRScanPage"));
const DepartmentPage = lazy(() => import("../features/department/components/DepartmentPage"));
const ProjectTimeLogsPage = lazy(() => import("../features/project-time-log/components/ProjectTimeLogsPage"));

export interface NavModule {
    name: string;
    path: string;
    feature?: string;
    elements: NavElement[];
    hideInMenu?: boolean;
}

export interface NavElement {
    id?: string;
    absolutePath?: string;
    name: string;
    path: string;
    icon: string;
    permission?: string;
    allowAnonymous?: boolean;
    feature?: string;
    component?: any;
    subElements?: NavElement[];
    hideInMenu?: boolean;
}

export const MAIN_NAV: NavModule[] = [
    {
        name: "Accounting",
        path: "accounting",
        elements: [
            {
                name: "Transaction Entry",
                path: "transaction-entry",
                icon: "fa-pen-to-square",
                component: <TransactionEntryPage />,
                permission: SapPermissions.transactionEntry,
                feature: Features.Accounting.Transactions,
            },
            {
                name: "Transactions",
                path: "transactions",
                icon: "fa-chart-line",
                component: <TransactionPage />,
                permission: SapPermissions.transactionView,
                feature: Features.Accounting.Transactions,
            },
            {
                name: "Invoices",
                path: "invoices",
                icon: "fa-file-invoice-dollar",
                component: <InvoicesPage />,
                feature: Features.Accounting.Invoices,
                permission: SapPermissions.invoicesView,
            },
            {
                name: "Vouchers",
                path: "vouchers",
                icon: "fa-ticket",
                component: <VouchersPage />,
                permission: SapPermissions.vouchersView,
                feature: Features.Accounting.Vouchers,
            },
        ],
    },
    {
        name: "Sales",
        path: "sales",
        elements: [
            {
                name: "Products",
                path: "products",
                icon: "fa-box-archive",
                component: <ProductsPage />,
                feature: Features.Accounting.Invoices,
                permission: SapPermissions.productsView,
            },
            {
                name: "Customers",
                path: "customers",
                icon: "fa-people-arrows",
                component: <CustomersPage />,
                feature: Features.Accounting.Invoices,
                permission: SapPermissions.customersView,
            },
        ],
    },
    {
        name: "Hr",
        path: "hr",
        elements: [
            {
                name: "Employees",
                path: "employees",
                icon: "fa-person-rays",
                component: <EmployeesPage />,
                feature: Features.HR.EmployeeManagement,
                permission: SapPermissions.employeesView,
            },
            {
                name: "QR Attendance",
                path: "qr-attendance",
                icon: "fa-qrcode",
                component: <QRScanPage />,
                permission: SapPermissions.attendanceQRScan,
                feature: Features.HR.AttendanceQR,
            },
            {
                name: "My Work Logs",
                path: "my-work-logs",
                icon: "fa-person-walking",
                component: <MyWorkLogsPage />,
                permission: SapPermissions.workLogsSelfView,
                feature: Features.HR.WorkLogs,
            },
            {
                name: "Work Logs",
                path: "work-logs",
                icon: "fa-person-walking-luggage",
                component: <WorkLogsPage />,
                feature: Features.HR.WorkLogs,
                permission: SapPermissions.workLogsView,
            },
            {
                name: "Work Summary",
                path: "work-summary",
                icon: "fa-clipboard-list",
                component: <WorkLogsSummaryPage />,
                feature: Features.HR.WorkLogs,
                permission: SapPermissions.workLogsView,
            },
            {
                name: "Week Planner",
                path: "week-planner",
                icon: "fa-calendar-days",
                component: <EmployeeWorkSchedulesPage />,
                feature: Features.HR.CustomWeeklySchedules,
                permission: SapPermissions.employeesFullAccess,
            },
            {
                name: "Shift Planner",
                path: "shift-planner",
                icon: "fa-calendar-days",
                component: <ShiftPlannerPage />,
                feature: Features.HR.WorkLogsShiftPlanning,
                permission: SapPermissions.employeesFullAccess,
            },
            {
                name: "Reports",
                path: "reports",
                icon: "fa-file",
                component: <HRReportsPage />,
                feature: Features.HR.EmployeeManagement,
                permission: SapPermissions.employeesView,
            },
        ],
    },
    {
        name: "Leave",
        path: "leave",
        elements: [
            {
                name: "My Leave",
                path: "my-leave",
                icon: "fa-umbrella-beach",
                component: <MyLeavePage />,
                permission: SapPermissions.leaveRequest,
                feature: Features.HR.LeaveManagement,
            },
            {
                name: "Leave Approve",
                path: "approve",
                icon: "fa-plane-arrival",
                component: <MyLeaveApprovePage />,
                feature: Features.HR.LeaveManagement,
                permission: SapPermissions.leaveApprove,
            },
            {
                name: "Leave Requests",
                path: "requests",
                icon: "fa-plane-departure",
                component: <LeaveApprovePage />,
                feature: Features.HR.LeaveManagement,
                permission: SapPermissions.leaveFinalApprove,
            },
            {
                name: "Manage Leave",
                path: "manage",
                icon: "fa-people-roof",
                component: <LeaveManagementPage />,
                feature: Features.HR.LeaveManagement,
                permission: SapPermissions.leaveManage,
            },
        ],
    },
    {
        name: "Finance",
        path: "finance",
        elements: [
            {
                name: "Payroll Adhocs",
                path: "payroll-ad-hocs",
                icon: "fa-hand-holding-dollar",
                component: <PayrollAddHocPage />,
                feature: Features.HR.Payroll,
                permission: SapPermissions.payrollFullAccess,
            },
            {
                name: "Payroll",
                path: "payroll",
                icon: "fa-file-invoice-dollar",
                component: <PayrollPage />,
                feature: Features.HR.Payroll,
                permission: SapPermissions.payrollFullAccess,
            },
        ],
    },
    {
        name: "Stock Market",
        path: "stock-market",
        elements: [
            {
                name: "Announcements",
                path: "announcements",
                icon: "fa-bullhorn",
                component: <StockMarketAnnouncementsPage />,
                permission: SapPermissions.stockMarketEventAnalytics,
                feature: Features.StockMarket.EventAnalytics,
            },
        ],
    },
    {
        name: "Farming",
        path: "farming",
        elements: [
            {
                name: "Harvest Log",
                icon: "fa-wheat-awn",
                path: "harvest-log",
                component: <HarvestLogsPage />,
                permission: SapPermissions.harvestLogFullAccess,
                feature: Features.Farming.HarvestLog,
            },
            {
                name: "Crop Tracking",
                icon: "fa-seedling",
                path: "crop-tracking",
                permission: SapPermissions.cropTrackingFullAccess,
                feature: Features.Farming.CropTracking,
                subElements: [
                    {
                        name: "Trees",
                        icon: "fa-tree",
                        path: "trees",
                        component: <TreesPage />,
                    },
                    {
                        name: "Seasons",
                        icon: "fa-bars-staggered",
                        path: "cropping-seasons",
                        component: <CropingSeasonsPage />,
                    },
                    {
                        name: "Crop Logs",
                        icon: "fa-chart-bar",
                        path: "logs",
                        component: <CropStatsPage />,
                    },
                    {
                        name: "Crop Analytics",
                        icon: "fa-chart-line",
                        path: "analytics",
                        component: <CropAnalyticsPage />,
                    },
                ],
            },
        ],
    },
    {
        name: "Projects",
        path: "projects",
        elements: [
            {
                name: "Projects",
                path: "projects",
                icon: "fa-house",
                component: <ProjectsPage />,
                feature: Features.Misc.Projects,
                permission: SapPermissions.projectsFullAccess,
            },
            {
                name: "Time Sheets",
                path: "project-time-sheets",
                icon: "fa-business-time",
                component: <ProjectTimeLogsPage selfMode={false} />,
                feature: Features.Misc.ProjectTimeLogs,
                permission: SapPermissions.ProjectTimeLogsFullAccess,
            },
            {
                name: "My Time Sheets",
                path: "my-project-time-sheets",
                icon: "fa-clock",
                component: <ProjectTimeLogsPage selfMode={true} />,
                feature: Features.Misc.ProjectTimeLogs,
                permission: SapPermissions.ProjectTimeLogsSelfAccess,
            },
        ],
    },
    {
        name: "Biometric Devices",
        path: "devices",
        elements: [
            {
                name: "Device Logs",
                path: "device-logs",
                icon: "fa-fingerprint",
                feature: Features.HR.AttendanceDevices,
                component: <AttendanceLogsPage />,
                permission: SapPermissions.attendanceLogsView,
            },
            {
                name: "Devices",
                icon: "fa-mobile-button",
                path: "device-fleet",
                component: <DevicesPage />,
                permission: SapPermissions.attendanceDevicesView,
                feature: Features.HR.AttendanceDevices,
            },
            {
                name: "QR Codes",
                icon: "fa-qrcode",
                path: "qr-codes",
                component: <QRCodeManagePage />,
                feature: Features.HR.AttendanceQR,
                permission: SapPermissions.attendanceQRManage,
            },
        ],
    },
    {
        name: "REFERRALS",
        path: "referrals",
        elements: [
            {
                name: "Referrals",
                path: "my",
                icon: "fa-user-friends",
                component: <MyReferralsPage />,
                permission: SapPermissions.referralProgramMember,
            },
        ],
    },
    {
        name: "ADMIN",
        path: "admin",
        elements: [
            {
                name: "Accounting",
                path: "accounting",
                icon: "fa-money-bill-wave",
                permission: SapPermissions.adminControl,
                subElements: [
                    {
                        name: "Expense Types",
                        path: "expense-types",
                        icon: "fa-money-bill-wave",
                        component: (
                            <LookupList
                                headerCode={LookupTypes.ExpenseTypes}
                                lookupTitle="Expense Types"
                                showSubTypes
                            />
                        ),
                        feature: Features.Accounting.Transactions,
                        permission: SapPermissions.transactionEntry,
                    },
                    {
                        name: "Income Types",
                        path: "income-types",
                        icon: "fa-sack-dollar",
                        component: (
                            <LookupList headerCode={LookupTypes.IncomeTypes} lookupTitle="Income Types" showSubTypes />
                        ),
                        feature: Features.Accounting.Transactions,
                        permission: SapPermissions.transactionEntry,
                    },
                ],
            },
            {
                name: "Farming",
                path: "farming",
                icon: "fa-seedling",
                permission: SapPermissions.adminControl,
                subElements: [
                    {
                        name: "Crop Fallen Reasons",
                        path: "crop-fallen-reasons",
                        icon: "fa-lemon",
                        component: (
                            <LookupList headerCode={LookupTypes.CropFallenReasons} lookupTitle="Crop Fallen Reasons" />
                        ),
                        feature: Features.Farming.AdvancedCropTracking,
                        permission: SapPermissions.cropTrackingFullAccess,
                    },
                ],
            },
            {
                name: "HR",
                path: "hr",
                icon: "fa-person-rays",
                permission: SapPermissions.adminControl,
                subElements: [
                    {
                        name: "Holidays",
                        icon: "fa-umbrella-beach",
                        path: "holidays",
                        component: <HolidaysPage />,
                        feature: Features.HR.WorkLogs,
                    },
                    {
                        name: "Leave Types",
                        icon: "fa-suitcase-rolling",
                        path: "leave-types",
                        component: <LeaveTypesPage />,
                        permission: SapPermissions.leaveTypesFullAccess,
                        feature: Features.HR.LeaveManagement,
                    },
                    {
                        name: "Designations",
                        path: "designations",
                        icon: "fa-id-badge",
                        component: <LookupList headerCode={LookupTypes.Designations} lookupTitle="Designations" />,
                        feature: Features.HR.EmployeeManagement,
                        permission: SapPermissions.employeesFullAccess,
                    },
                    {
                        name: "Departments",
                        path: "departments",
                        icon: "fa-building-columns",
                        component: <DepartmentPage />,
                        feature: Features.HR.EmployeeManagement,
                        permission: SapPermissions.employeesFullAccess,
                    },
                    {
                        name: "Teams",
                        path: "teams",
                        icon: "fa-users",
                        component: <TeamsPage />,
                        feature: Features.HR.Teams,
                        permission: SapPermissions.employeesFullAccess,
                    },
                    {
                        name: "Branches",
                        path: "branches",
                        icon: "fa-building",
                        component: <BranchesPage />,
                        feature: Features.HR.MultiBranch,
                        permission: SapPermissions.employeesFullAccess,
                    },
                ],
            },
            {
                name: "Payroll",
                path: "payroll",
                icon: "fa-hand-holding-dollar",
                permission: SapPermissions.adminControl,
                feature: Features.HR.Payroll,
                subElements: [
                    {
                        name: "Fixed Allowances",
                        icon: "fa-money-check-dollar",
                        path: "fixed-allowance-types",
                        component: <SalaryComponentTypesPage category={SalaryComponentCategory.Allowance} />,
                        permission: SapPermissions.employeesSalaryAdmin,
                        feature: Features.HR.Payroll,
                    },
                    {
                        name: "Fixed Deductions",
                        icon: "fa-money-bills",
                        path: "fixed-deduction-types",
                        component: <SalaryComponentTypesPage category={SalaryComponentCategory.Deduction} />,
                        permission: SapPermissions.employeesSalaryAdmin,
                        feature: Features.HR.Payroll,
                    },
                    {
                        name: "Adhoc Allowances",
                        icon: "fa-sack-dollar",
                        path: "ad-hoc-allowance-types",
                        component: (
                            <LookupList
                                headerCode={LookupTypes.AddHocAllowanceTypes}
                                lookupTitle="Ad-hoc Allowance Types"
                            />
                        ),
                        permission: SapPermissions.employeesSalaryAdmin,
                        feature: Features.HR.Payroll,
                    },
                    {
                        name: "Adhoc Deductions",
                        icon: "fa-sack-xmark",
                        path: "ad-hoc-deduction-types",
                        component: (
                            <LookupList
                                headerCode={LookupTypes.AddHocDeductionTypes}
                                lookupTitle="Ad-hoc Deduction Types"
                            />
                        ),

                        permission: SapPermissions.employeesSalaryAdmin,
                        feature: Features.HR.Payroll,
                    },
                ],
            },
            {
                name: "General",
                path: "general",
                icon: "fa-gear",
                permission: SapPermissions.adminControl,
                subElements: [
                    {
                        name: "Company Details",
                        path: "company-details",
                        icon: "fa-landmark",
                        component: <CurrentTenantPage />,
                        permission: SapPermissions.tenantConfigView,
                    },
                    {
                        name: "Users",
                        path: "users",
                        icon: "fa-users",
                        component: <TenantUsersPage />,
                        permission: SapPermissions.usersFullAccess,
                    },
                    {
                        name: "Configurations",
                        path: "configurations",
                        icon: "fa-gear",
                        component: <TenantConfigsPage />,
                        permission: SapPermissions.tenantConfigView,
                    },
                    {
                        name: "Billing",
                        path: "billing",
                        icon: "fa-file-invoice-dollar",
                        component: <TenantBillingPage />,
                        permission: SapPermissions.tenantConfigView,
                    },
                ],
            },
        ],
    },
    {
        name: "Tenant Admin",
        path: "system-admin",
        elements: [
            {
                name: "Tenants",
                icon: "fa-building",
                path: "tenants",
                component: <AllTenantsPage />,
                permission: SapPermissions.manageTenants,
            },
            {
                name: "Devices",
                icon: "fa-mobile-button",
                path: "attendance-devices",
                component: <DevicesPage all />,
                permission: SapPermissions.attendanceDevicesFullAccess,
            },
            {
                name: "Billing",
                icon: "fa-file-invoice-dollar",
                path: "billing",
                component: <TenantInvoicesPage />,
                permission: SapPermissions.manageTenants,
            },
            {
                name: "Referrals",
                icon: "fa-medal",
                path: "referral-codes",
                component: <ReferralCodesPage />,
                permission: SapPermissions.referralProgramManage,
            },
        ],
    },
    {
        name: "User",
        path: "user",
        hideInMenu: true,
        elements: [
            {
                name: "Profile",
                icon: "",
                path: "profile",
                component: <UserProfilePage />,
            },
        ],
    },
    {
        name: "Home",
        path: "home",
        hideInMenu: true,
        elements: [
            {
                name: "Dashboard",
                path: "dashboard",
                icon: "fa-gauge-high",
                component: <SmartDashboardPage />,
            },
        ],
    },
];
